import { cn } from '../utils/cn';

type SkeletonProps = {
    className?: string;
};

/** Bloco base de skeleton (pulse). */
export function Skeleton({ className }: SkeletonProps) {
    return (
        <div
            className={cn(
                'animate-pulse rounded-md bg-gray-200/80 dark:bg-zinc-700/80',
                className,
            )}
            aria-hidden
        />
    );
}

type PageSkeletonProps = {
    /** Layout tipico da area de conteudo */
    variant?: 'page' | 'table' | 'cards' | 'detail' | 'dashboard' | 'inline';
    className?: string;
    /** Quantidade de linhas/cards (quando aplicavel) */
    count?: number;
};

/**
 * Placeholder de carregamento por blocos — padrao de loading do v2.
 * Preferir isto (ou LoadingState) a spinner/texto "Carregando…".
 */
export function PageSkeleton({
    variant = 'page',
    className,
    count,
}: PageSkeletonProps) {
    if (variant === 'inline') {
        return (
            <div className={cn('flex items-center gap-3 py-4', className)} role="status" aria-label="Carregando">
                <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-24" />
            </div>
        );
    }

    if (variant === 'table') {
        const rows = count ?? 6;
        return (
            <div className={cn('space-y-3 p-4', className)} role="status" aria-label="Carregando">
                <div className="flex justify-end gap-2">
                    <Skeleton className="h-9 w-72" />
                    <Skeleton className="h-9 w-9" />
                </div>
                <Skeleton className="h-10 w-full" />
                {Array.from({ length: rows }, (_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                ))}
            </div>
        );
    }

    if (variant === 'cards') {
        const cards = count ?? 4;
        return (
            <div
                className={cn('grid gap-3 sm:grid-cols-2 lg:grid-cols-4', className)}
                role="status"
                aria-label="Carregando"
            >
                {Array.from({ length: cards }, (_, i) => (
                    <div key={i} className="space-y-3 rounded-xl border border-gray-100 p-3 dark:border-zinc-800">
                        <Skeleton className="h-28 w-full rounded-lg" />
                        <Skeleton className="h-3 w-1/3" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                    </div>
                ))}
            </div>
        );
    }

    if (variant === 'detail') {
        return (
            <div className={cn('space-y-6', className)} role="status" aria-label="Carregando">
                <div className="space-y-2">
                    <Skeleton className="h-8 w-72" />
                    <Skeleton className="h-4 w-48" />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                    <Skeleton className="h-40 w-full rounded-xl" />
                    <Skeleton className="h-40 w-full rounded-xl" />
                </div>
                <Skeleton className="h-64 w-full rounded-xl" />
            </div>
        );
    }

    if (variant === 'dashboard') {
        return (
            <div className={cn('space-y-6', className)} role="status" aria-label="Carregando">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {Array.from({ length: 4 }, (_, i) => (
                        <Skeleton key={i} className="h-24 w-full rounded-xl" />
                    ))}
                </div>
                <Skeleton className="h-64 w-full rounded-2xl" />
                <div className="grid gap-4 lg:grid-cols-2">
                    <Skeleton className="h-48 w-full rounded-xl" />
                    <Skeleton className="h-48 w-full rounded-xl" />
                </div>
            </div>
        );
    }

    // page (default)
    return (
        <div className={cn('space-y-6', className)} role="status" aria-label="Carregando">
            <div className="space-y-2">
                <Skeleton className="h-8 w-56" />
                <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-36 w-full rounded-2xl" />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: count ?? 3 }, (_, i) => (
                    <Skeleton key={i} className="h-32 w-full rounded-xl" />
                ))}
            </div>
        </div>
    );
}
